import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mup/public/firma`;

export default function MupFirmaPubblica() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [letto, setLetto] = useState(false);
  const [otp, setOtp] = useState("");
  const [dest, setDest] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [done, setDone] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/${token}`).then(({ data }) => { setInfo(data); if (data.signed) setDone({ signed_at: data.signed_at }); })
      .catch((e) => setErr(e.response?.data?.detail || "Link non valido o scaduto"));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("focus", load); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const apri = () => { window.open(`${API}/${token}/pdf`, "_blank"); setTimeout(load, 1500); };
  const requestOtp = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/${token}/request-otp`);
      setDest(data.destinazione || ""); setSent(true); setResendIn(data.cooldown || 60);
    } catch (e) { setErr(e.response?.data?.detail || "Errore nell'invio del codice"); }
    finally { setBusy(false); }
  };
  const sign = async () => {
    setBusy(true); setErr("");
    try { const { data } = await axios.post(`${API}/${token}/sign`, { otp, presa_visione: letto }); setDone(data); }
    catch (e) { setErr(e.response?.data?.detail || "Codice non valido"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="mup-firma-pubblica">
        <div className="text-xs font-semibold tracking-wide text-teal-700 mb-1">ASSIETICA · FIRMA DOCUMENTO</div>
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Lettura e firma del MUP</h1>
        {err && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="mupf-err">{err}</div>}

        {done ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-emerald-800" data-testid="mupf-done">
            <p className="font-semibold">Firma completata. Grazie!</p>
            <p className="text-sm mt-1">{info?.ha_email ? "Il documento firmato le è stato inviato via email. Può scaricarlo anche da qui." : "Può scaricare qui la copia del documento firmato."}</p>
            <button onClick={apri} className="mt-3 text-sm font-medium text-emerald-700 underline" data-testid="mupf-scarica-firmato">Scarica il documento firmato</button>
          </div>
        ) : info ? (
          <>
            <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700 space-y-0.5 mb-4" data-testid="mupf-info">
              <div>Documento <b>{info.code}</b></div>
              {info.cliente && <div>Contraente: <b>{info.cliente}</b></div>}
              {info.prodotto && <div>Prodotto: {info.prodotto}{info.compagnia ? ` — ${info.compagnia}` : ""}</div>}
              {info.intermediario && <div>Intermediario: {info.intermediario}</div>}
            </div>
            <ol className="space-y-4 text-sm">
              <li>
                <p className="font-medium text-slate-900 mb-1.5">1. Legga il documento</p>
                <button onClick={apri} data-testid="mupf-apri-pdf" className="w-full border border-teal-600 text-teal-700 rounded-lg py-2.5 font-medium hover:bg-teal-50">
                  Apri il documento (PDF)
                </button>
                {info.letto && <p className="text-xs text-emerald-700 mt-1" data-testid="mupf-letto">Documento aperto</p>}
              </li>
              <li>
                <p className="font-medium text-slate-900 mb-1.5">2. Confermi la presa visione</p>
                <label className={`flex items-start gap-2 ${info.letto ? "" : "opacity-50"}`}>
                  <input type="checkbox" className="mt-1" checked={letto} disabled={!info.letto} onChange={(e) => setLetto(e.target.checked)} data-testid="mupf-presa-visione" />
                  <span className="text-slate-600">Dichiaro di aver ricevuto e letto il documento {info.code}, comprensivo dell'informativa precontrattuale, e di volerlo sottoscrivere.</span>
                </label>
              </li>
              <li>
                <p className="font-medium text-slate-900 mb-1.5">3. Firmi con il codice ricevuto via SMS</p>
                {!sent ? (
                  <button data-testid="mupf-request-otp" onClick={requestOtp} disabled={busy || !letto}
                    className="w-full bg-teal-700 text-white rounded-lg py-2.5 font-medium disabled:opacity-50">
                    {busy ? "Invio…" : `Ricevi il codice via SMS${info.destinazione ? ` (${info.destinazione})` : ""}`}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-slate-600">Codice inviato{dest ? <> al numero <b>{dest}</b></> : ""}. È valido 10 minuti.</p>
                    <input data-testid="mupf-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      inputMode="numeric" maxLength={6} placeholder="Codice OTP a 6 cifre"
                      className="w-full border border-slate-300 rounded-lg p-2.5 text-center text-lg tracking-widest" />
                    <button data-testid="mupf-sign-btn" onClick={sign} disabled={busy || otp.length < 6 || !letto}
                      className="w-full bg-emerald-600 text-white rounded-lg py-2.5 font-medium disabled:opacity-50">
                      {busy ? "Firma in corso…" : "Firma il documento"}
                    </button>
                    <button data-testid="mupf-resend" onClick={requestOtp} disabled={resendIn > 0 || busy} className="w-full text-sm text-teal-700 disabled:text-slate-400">
                      {resendIn > 0 ? `Reinvia SMS tra ${resendIn}s` : "Non ha ricevuto il codice? Reinvia SMS"}
                    </button>
                  </div>
                )}
              </li>
            </ol>
            <p className="text-xs text-slate-400 mt-5">Firma elettronica semplice con codice OTP monouso inviato via SMS (Reg. UE 910/2014 e CAD). Vengono registrati data e ora, indirizzo IP, dispositivo e impronta del documento.</p>
          </>
        ) : !err ? <p className="text-slate-500">Caricamento…</p> : null}
      </div>
    </div>
  );
}
